import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Check } from "lucide-react";
import type { Lang } from "@/i18n";
import { path } from "@/lib/site";
import { Reveal } from "../Reveal";
import { useServices } from "@/lib/usePublicContent";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";
import { Marquee } from "../Marquee";
import { MagneticButton } from "../MagneticButton";

interface ServiceItem {
  id: string;
  num: string;
  title: string;
  intro: string;
  bullets: string[];
  tags: string[];
  modality?: string;
  tldr?: string;
}

interface WhyItem { e: string; t: string; d: string }
interface FaqItem { q: string; a: string }
interface TableRow { l: string; v: string[] }

export function ServicesPage({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const fallback = t("services.items", { returnObjects: true }) as ServiceItem[];
  const { data: dbServices } = useServices(lang);
  const items: ServiceItem[] =
    dbServices && dbServices.length > 0
      ? dbServices.map((s) => ({
          id: s.slug,
          num: s.num,
          title: s.title,
          intro: s.intro,
          bullets: s.bullets,
          tags: s.tags,
          modality: s.modality,
        }))
      : fallback;

  return (
    <>
      <Breadcrumbs lang={lang} current="services" />
      {/* HERO — Brutalist split */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
              / Services — 03
            </span>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="lg:col-span-8">
              <h1 className="font-display font-black uppercase leading-[0.88] tracking-[-0.045em] text-foreground text-[clamp(2.5rem,8vw,7rem)]">
                {t("services.h1")}
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / {t("services.tldrLabel")}
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">
                  {t("services.tldr")}
                </p>
                <p className="mt-5 text-sm text-foreground/70">{t("services.sub")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        variant="ink"
        items={[
          "Operations",
          "Pre-opening",
          "Audit · Action plan",
          "Permits · IT · Automation",
          "Belgium · Luxembourg",
        ]}
      />

      {/* OFFERS — alternating split bento per service */}
      {items.map((item, idx) => (
        <section
          key={item.id}
          id={item.id}
          className="section scroll-mt-24 border-b-[1.5px] border-foreground"
        >
          <div className="container-page">
            <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
              <Reveal className="min-w-0 lg:col-span-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-foreground/60">
                    {item.num} / 03
                  </span>
                  {idx === 0 && (
                    <span className="bg-blaze px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#0a0a0a]">
                      Flagship
                    </span>
                  )}
                </div>
                <h2 className="font-display mt-8 break-words [overflow-wrap:anywhere] hyphens-auto text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-5xl xl:text-6xl">
                  {item.title}
                </h2>
                <p className="mt-6 text-base leading-relaxed text-foreground/80 md:text-lg">
                  {item.intro}
                </p>
                {item.tldr && (
                  <div className="bento-tile mt-8 p-6 md:p-7">
                    <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                      / {t("services.tldrLabel")}
                    </span>
                    <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">
                      {item.tldr}
                    </p>
                  </div>
                )}
                <div className="mt-8 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="border-[1.5px] border-foreground px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-8">
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                    / {t("services.modality")}
                  </span>
                  <p className="mt-2 text-sm text-foreground/75">
                    {item.modality || t("services.modalityValue")}
                  </p>
                </div>
                <MagneticButton>
                  <Link
                    to={path(lang, "contact")}
                    className="btn-blaze mt-10 inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em]"
                  >
                    {t("common.talk")} <ArrowUpRight size={14} />
                  </Link>
                </MagneticButton>
              </Reveal>

              <Reveal delay={0.08} className="min-w-0 lg:col-span-7">
                <div
                  className={`bento-tile ${idx === 0 ? "is-ink" : idx === 2 ? "is-spark" : ""} tile-fx is-hover group flex h-full flex-col p-8 md:p-12`}
                >
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60">
                    / {t("services.concretely")}
                  </span>
                  <ul className="mt-8 space-y-4">
                    {item.bullets.map((b, i) => (
                      <li
                        key={i}
                        className="flex gap-4 border-b border-current/15 pb-4 last:border-0 last:pb-0"
                      >
                        <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center border-[1.5px] border-current">
                          <Check size={11} strokeWidth={3} />
                        </span>
                        <span className="text-[15px] leading-relaxed opacity-90">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      {/* Counter-marquee */}
      <Marquee
        variant="blaze"
        reverse
        items={[
          "Forfait · Régie · Retainer",
          "SOPs · KPIs · ROI",
          "Belgium · Luxembourg",
        ]}
      />

      {/* WHY 2AM — Numbered bento strip (Méthode pattern) */}
      <section className="section border-b-[1.5px] border-foreground bg-foreground text-background">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
              / Why 2AM
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
              {t("services.whyTitle")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(t("services.why", { returnObjects: true }) as WhyItem[]).map((w, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-[1.5px] border-background bg-foreground p-7 h-full transition-colors hover:bg-background hover:text-foreground">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-5xl font-black tracking-[-0.04em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-2 w-2 bg-blaze" aria-hidden />
                  </div>
                  <h3 className="font-display mt-8 text-xl font-bold uppercase tracking-[-0.01em]">
                    {w.t}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed opacity-75">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHORT-STAY & HYBRID */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page grid gap-10 lg:grid-cols-12 lg:gap-12">
          <Reveal className="lg:col-span-5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Short-stay &amp; hybrid
            </span>
            <h2 className="font-display mt-3 text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-6xl">
              {t("services.shortStayTitle")}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/80 md:text-lg">
              {t("services.shortStayDesc")}
            </p>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-7">
            <ul className="bento-tile tile-fx is-hover group space-y-4 p-8 md:p-12">
              {(t("services.shortStayBullets", { returnObjects: true }) as string[]).map((b, i) => (
                <li
                  key={i}
                  className="flex gap-4 border-b border-foreground/15 pb-4 last:border-0 last:pb-0"
                >
                  <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center border-[1.5px] border-foreground">
                    <Check size={11} strokeWidth={3} />
                  </span>
                  <span className="text-[15px] leading-relaxed text-foreground/85">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON TABLE — brutalist grid */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / Compare — 03
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-6xl">
              {t("services.tableTitle")}
            </h2>
          </Reveal>
          <div className="mt-12 overflow-x-auto border-[1.5px] border-foreground">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="bg-foreground text-background">
                  {(t("services.tableHeaders", { returnObjects: true }) as string[]).map((h, i) => (
                    <th
                      key={i}
                      className="border-r-[1.5px] border-background/30 p-5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] last:border-r-0"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(t("services.tableRows", { returnObjects: true }) as TableRow[]).map((row, i) => (
                  <tr
                    key={i}
                    className="border-t-[1.5px] border-foreground first:border-t-0"
                  >
                    <th
                      scope="row"
                      className="border-r-[1.5px] border-foreground p-5 align-top font-display text-sm font-bold uppercase tracking-[-0.01em] text-foreground"
                    >
                      {row.l}
                    </th>
                    {row.v.map((v, j) => (
                      <td
                        key={j}
                        className="border-r-[1.5px] border-foreground p-5 align-top text-foreground/80 last:border-r-0"
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* MINI-FAQ — numbered brutalist cards */}
      <section className="section border-b-[1.5px] border-foreground bg-[var(--surface)]">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / FAQ
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-6xl">
              {t("services.miniFaqTitle")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {(t("services.miniFaq", { returnObjects: true }) as FaqItem[]).map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="bento-tile tile-fx is-hover group flex h-full flex-col p-7">
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                    0{i + 1} / 0{(t("services.miniFaq", { returnObjects: true }) as FaqItem[]).length}
                  </span>
                  <h3 className="font-display mt-6 text-xl font-black uppercase leading-[1.1] tracking-[-0.02em]">
                    {f.q}
                  </h3>
                  <p className="mt-4 text-[14px] leading-relaxed text-foreground/75">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA — Brutalist block */}
      <section className="section relative overflow-hidden bg-foreground text-background">
        <div className="container-page relative">
          <div className="grid gap-10 md:grid-cols-12 md:items-end">
            <Reveal className="md:col-span-8">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
                / {t("common.bookCall")}
              </span>
              <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.92] tracking-[-0.04em] md:text-7xl">
                {t("services.faqLinkTitle")}
              </h2>
              <p className="mt-6 max-w-xl text-base text-background/75 md:text-lg">
                {t("services.faqLinkText")}
              </p>
            </Reveal>
            <Reveal delay={0.08} className="md:col-span-4">
              <MagneticButton strength={0.4}>
                <Link
                  to={path(lang, "contact")}
                  className="btn-blaze inline-flex w-full items-center justify-between gap-2 px-6 py-4 text-sm font-bold uppercase tracking-[0.12em]"
                >
                  {t("common.bookSlot")} <ArrowUpRight size={18} />
                </Link>
              </MagneticButton>
            </Reveal>
          </div>
        </div>
      </section>
      <RelatedLinks lang={lang} items={["references", "tools", "about"]} />
    </>
  );
}
